import React from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { Result, Skeleton } from 'antd';
import { LockOutlined } from '@ant-design/icons';
import { useRbac } from '../../contexts/RbacContext';
import type { ResourceKey } from '../../config/rbac';
import type { PermissionValue } from '../../config/rbac';

interface ProtectedRouteProps {
  resource: ResourceKey;
  permission: PermissionValue | PermissionValue[];
  children: React.ReactNode;
  redirectTo?: string;
  fallback?: React.ReactNode;
  /** Разрешить доступ если у пользователя есть права на любой дочерний ресурс */
  checkChildren?: boolean;
}

export const ProtectedRoute: React.FC<ProtectedRouteProps> = ({
  resource,
  permission,
  children,
  redirectTo,
  fallback,
  checkChildren = false,
}) => {
  const { hasPermission, hasAnyPermission, hasAnyChildPermission, isLoading, currentUser } = useRbac();
  const location = useLocation();

  if (isLoading) {
    return (
      <div style={{ padding: '48px' }}>
        <Skeleton active paragraph={{ rows: 8 }} />
      </div>
    );
  }

  if (!currentUser) {
    return <Navigate to="/login" replace state={{ from: location }} />;
  }

  let hasAccess: boolean;
  if (checkChildren) {
    const perm = Array.isArray(permission) ? permission[0] : permission;
    hasAccess = hasAnyChildPermission(resource, perm);
  } else {
    hasAccess = Array.isArray(permission)
      ? hasAnyPermission(resource, permission)
      : hasPermission(resource, permission);
  }

  if (!hasAccess) {
    if (redirectTo) {
      return <Navigate to={redirectTo} replace state={{ from: location }} />;
    }

    if (fallback) {
      return <>{fallback}</>;
    }

    return (
      <div style={{ padding: '48px' }}>
        <Result
          status="403"
          icon={<LockOutlined />}
          title="Доступ запрещён"
          subTitle="У вас нет прав для доступа к этому разделу. Обратитесь к администратору."
        />
      </div>
    );
  }

  return <>{children}</>;
};


